import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, Sparkles, Award, Gem, Shield, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProductCard from '@/components/ProductCard';
import { LoopVideo } from '@/components/LoopVideo';
import { ArgentoHomeSection } from '@/components/ArgentoHomeSection';
import { useScrollAnimation } from '@/hooks/useAnimations';
import { useSeo } from '@/hooks/useSeo';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function HomePage() {
  useSeo({
    title: "Ciro Gallotti — L'Arte del Gioiello",
    description:
      "Gioielleria di lusso a Napoli dal 1968. Anelli, collane, bracciali e orecchini in oro, diamanti e pietre preziose. Scopri la collezione o prenota un appuntamento privato nello showroom di Via Chiaia.",
    path: '/',
  });

  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [igProducts, setIgProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [homeContent, setHomeContent] = useState({});
  const heroRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  // Helper CMS: usa il valore salvato in admin, altrimenti il default originale
  const c = (key, fallback) => {
    const v = homeContent[key];
    return v !== undefined && v !== null && String(v).trim() !== '' ? v : fallback;
  };

  const section1 = useScrollAnimation(0.2);
  const section2 = useScrollAnimation(0.2);
  const section3 = useScrollAnimation(0.2);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [featuredRes, allRes] = await Promise.all([
          axios.get(`${API}/products?featured=true&collection=Generale`),
          axios.get(`${API}/products?collection=Generale`),
        ]);
        const all = allRes.data || [];
        const featured = featuredRes.data || [];

        // Fallback: se i featured sono < 4, riempi con altri prodotti
        // (con immagine, non duplicati) per non lasciare slot vuoti.
        const featuredIds = new Set(featured.map((p) => p.id));
        const fillers = all
          .filter((p) => p && p.image_url && !featuredIds.has(p.id))
          .slice(0, Math.max(0, 4 - featured.length));

        setFeaturedProducts([...featured, ...fillers].slice(0, 4));

        // Griglia Instagram: 6 prodotti reali con immagine
        const withImages = all.filter((p) => p && p.image_url);
        setIgProducts(withImages.slice(0, 6));
      } catch (e) {
        console.error('Error fetching products:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    axios.get(`${API}/site-content/home`)
      .then((res) => setHomeContent(res.data?.content || {}))
      .catch(() => {});
  }, []);

  return (
    <div className="bg-ivory" data-testid="home-page">
      {/* ============== HERO ============== */}
      <section
        ref={heroRef}
        className="relative h-screen min-h-[640px] flex items-end overflow-hidden bg-ink"
        data-testid="hero-section"
      >
        {/* Background video loop, subtle parallax */}
        <div
          className="absolute inset-0"
          style={{ transform: `translate3d(0, ${scrollY * 0.18}px, 0)` }}
          aria-hidden="true"
        >
          <LoopVideo
            src={c('hero_video', '/hero-loop.mp4')}
            poster={c('hero_poster', '/hero-poster.jpg')}
            className="w-full h-[115%] object-cover opacity-60"
            testid="hero-video"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/15" />
        </div>

        <div className="relative z-10 w-full max-w-container mx-auto px-6 md:px-12 lg:px-16 pb-20 md:pb-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-px bg-ivory" />
              <p className="text-eyebrow text-ivory animate-fade-in">
                {c('hero_eyebrow', "Gioielleria d'Arte dal 1968")}
              </p>
            </div>

            <h1 className="font-display text-display text-ivory leading-[0.95]">
              <span className="block animate-slide-up" style={{ animationDelay: '120ms' }}>
                {c('hero_title_1', "L'Arte del")}
              </span>
              <span
                className="block italic text-ivory/90 animate-slide-up"
                style={{ animationDelay: '240ms' }}
              >
                {c('hero_title_2', 'Gioiello')}
              </span>
            </h1>

            <p
              className="mt-10 max-w-xl text-white text-lg md:text-xl font-light leading-relaxed animate-fade-in"
              style={{ animationDelay: '420ms' }}
            >
              {c('hero_subtitle', "Ogni gioiello è un'opera d'arte unica, forgiata con passione e maestria nel cuore di Napoli.")}
            </p>

            <div
              className="mt-12 flex flex-col sm:flex-row gap-4 animate-fade-in"
              style={{ animationDelay: '560ms' }}
            >
              <Link to="/collezione" data-testid="hero-cta-collezione">
                <Button size="xl" variant="accent" className="w-full sm:w-auto group">
                  Scopri la Collezione
                  <ArrowRight className="w-4 h-4 ml-1 transition-transform duration-hover group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/appuntamento" data-testid="hero-cta-appuntamento">
                <Button
                  size="xl"
                  variant="outline"
                  className="w-full sm:w-auto border-ivory/30 text-ivory hover:bg-ivory hover:text-ink hover:border-ivory"
                >
                  Prenota Appuntamento
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-ivory/40">
          <span className="text-eyebrow">Scopri</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </div>
      </section>

      {/* ============== COLLEZIONE RAINBOW ============== */}
      <section className="pt-20 md:pt-28 px-6 md:px-12 lg:px-16" data-testid="rainbow-section">
        <div className="max-w-container mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
            <div className="max-w-2xl">
              <p className="text-eyebrow text-bordeaux mb-4">{c('rainbow_eyebrow', 'Nuova Collezione')}</p>
              <h2 className="font-display text-h1 text-ink leading-[0.95] tracking-tight">
                {c('rainbow_title', 'Collezione Rainbow')}
              </h2>
              <p className="text-base text-ink/70 leading-relaxed mt-5 max-w-reading">
                {c('rainbow_subtitle', "Croci in oro impreziosite da zaffiri multicolore taglio princess: un arcobaleno di pietre naturali che sfuma dal fuoco all'aurora. Ogni creazione è unica, realizzata a mano nei nostri laboratori orafi di Napoli.")}
              </p>
            </div>
            <Link
              to="/collezione"
              data-testid="rainbow-cta"
              className="group inline-flex items-center gap-2 text-eyebrow text-ink hover:text-bordeaux transition-colors duration-hover whitespace-nowrap"
            >
              {c('rainbow_cta', 'Scopri la Collezione')}
              <ArrowRight className="w-4 h-4 transition-transform duration-hover group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              { src: '/rainbow-1.jpg', cmsKey: 'rainbow_1', name: 'Aurora Hawaii', material: 'Oro Rosa', price: '680,00 €', to: '/prodotto/b279ee42-3a14-4bde-8d3c-403a6699f75a', testid: 'rainbow-hawaii' },
              { src: '/rainbow-2.jpg', cmsKey: 'rainbow_2', name: 'Aurora Rio', material: 'Oro Giallo', price: '680,00 €', to: '/prodotto/c6c44e85-a28c-4341-bba0-fed3c85a0823', testid: 'rainbow-rio' },
              { src: '/rainbow-3.jpg', cmsKey: 'rainbow_3', name: 'Aurora Tokyo', material: 'Oro Bianco', price: '680,00 €', to: '/prodotto/d0421c96-6a47-47cc-bfa3-b77e3aca3650', testid: 'rainbow-tokyo' },
            ].map((item) => (
              <Link to={item.to} key={item.testid} className="group block" data-testid={item.testid}>
                <div className="image-shine aspect-square overflow-hidden rounded-2xl shadow-sm">
                  <img
                    src={c(item.cmsKey, item.src)}
                    alt={`${item.name} — croce con zaffiri multicolore in ${item.material} — Ciro Gallotti`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="mt-4">
                  <p className="text-eyebrow text-bordeaux mb-1">{item.material}</p>
                  <p className="font-display text-h3 text-ink">{item.name}</p>
                  <p className="text-small text-ink/70 mt-1 tabular-nums">{item.price}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============== COLLEZIONE ARGENTO ============== */}
      <div className="pt-20 md:pt-28">
        <ArgentoHomeSection />
      </div>

      {/* ============== FEATURES ============== */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16">
        <div
          ref={section1.ref}
          className={`max-w-container mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children ${section1.isVisible ? 'visible' : ''}`}
        >
          {[
            { icon: Gem, title: 'Materiali Pregiati', desc: 'Solo oro 18kt e pietre certificate' },
            { icon: Award, title: 'Maestria Artigianale', desc: 'Tre generazioni di eccellenza' },
            { icon: Sparkles, title: 'Design Esclusivo', desc: 'Creazioni uniche ispirate a Napoli' },
            { icon: Shield, title: 'Garanzia a Vita', desc: 'Certificato di autenticità incluso' },
          ].map((item) => (
            <div
              key={item.title}
              className="group text-center p-8 md:p-10 rounded-3xl bg-ivory border border-line hover:border-bordeaux/40 hover:-translate-y-1 shadow-sm hover:shadow-lg transition-all duration-enter ease-out"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-bordeaux/10 group-hover:bg-bordeaux flex items-center justify-center transition-colors duration-hover ease-out">
                <item.icon className="w-7 h-7 text-bordeaux group-hover:text-ivory transition-colors duration-hover" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-h4 text-ink mb-2">{item.title}</h3>
              <p className="text-small text-muted leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============== FEATURED PRODUCTS ============== */}
      <section
        className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-secondary/40"
        data-testid="featured-section"
      >
        <div className="max-w-container mx-auto">
          <div
            ref={section2.ref}
            className={`flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 scroll-fade-up ${section2.isVisible ? 'visible' : ''}`}
          >
            <div>
              <p className="text-eyebrow text-bordeaux mb-4">Selezione Esclusiva</p>
              <h2 className="font-display text-h1 text-ink leading-tight">
                Gioielli <span className="italic">in Evidenza</span>
              </h2>
            </div>
            <Link
              to="/collezione"
              className="flex items-center gap-3 text-ink hover:text-bordeaux transition-colors duration-hover line-animation pb-1 w-fit"
              data-testid="view-all-products"
            >
              <span className="text-eyebrow">Vedi Tutti</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-line rounded-md mb-5" />
                  <div className="h-3 bg-line w-20 mb-3" />
                  <div className="h-5 bg-line w-40 mb-2" />
                  <div className="h-4 bg-line w-24" />
                </div>
              ))}
            </div>
          ) : (
            <div
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 stagger-children ${section2.isVisible ? 'visible' : ''}`}
            >
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============== STORY ============== */}
      <section
        className="py-20 md:py-32 px-6 md:px-12 lg:px-16"
        data-testid="story-section"
      >
        <div className="max-w-container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div
              ref={section3.ref}
              className={`order-2 lg:order-1 lg:col-span-6 scroll-fade-up ${section3.isVisible ? 'visible' : ''}`}
            >
              <p className="text-eyebrow text-bordeaux mb-4">La Nostra Storia</p>
              <h2 className="font-display text-h1 text-ink mb-8 leading-tight">
                Tradizione
                <br />
                <span className="italic">e Innovazione</span>
              </h2>
              <div className="space-y-5 text-ink/75 leading-relaxed mb-10 max-w-reading">
                <p className="text-lg">
                  {c('story_paragraph_1', "Da tre generazioni, la famiglia Gallotti crea gioielli che uniscono l'antica tradizione orafa napoletana con un design contemporaneo.")}
                </p>
                <p>
                  {c('story_paragraph_2', "Ogni creazione è un'opera d'arte unica, forgiata con passione nel cuore di Napoli, dove la maestria artigianale si tramanda di padre in figlio.")}
                </p>
              </div>
              <Link to="/chi-siamo" data-testid="story-cta">
                <Button size="lg" variant="default">
                  Scopri la Nostra Storia
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            <div className="order-1 lg:order-2 lg:col-span-6 relative">
              <div className="image-shine aspect-[4/5] overflow-hidden rounded-md">
                <LoopVideo
                  src={c('story_video', '/story-loop.mp4')}
                  poster={c('story_poster', '/story-poster.jpg')}
                  className="w-full h-full object-cover"
                  testid="story-video"
                />
              </div>
              <div className="absolute -bottom-6 left-6 md:left-auto md:-bottom-8 md:-left-8 bg-bordeaux text-ivory px-6 py-5 rounded-2xl shadow-lg hidden md:block">
                <p className="font-display text-4xl leading-none">50+</p>
                <p className="text-eyebrow text-ivory/80 mt-1">Anni di Eccellenza</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== INSTAGRAM CALLOUT ============== */}
      <section
        className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-secondary/40"
        data-testid="instagram-section"
      >
        <div className="max-w-container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-5">
              <p className="text-eyebrow text-bordeaux mb-4">{c('instagram_eyebrow', '@cirogallotti_jewerlydesign')}</p>
              <h2 className="font-display text-h1 text-ink mb-6 leading-tight">
                {c('instagram_title', 'Seguici su Instagram')}
              </h2>
              <p className="text-ink/70 leading-relaxed mb-8 max-w-md">
                {c('instagram_subtitle', 'Anteprime delle nuove creazioni, scatti dallo showroom e dietro le quinte del lavoro artigianale che da tre generazioni rende unici i nostri gioielli.')}
              </p>
              <a
                href="https://www.instagram.com/cirogallotti_jewerlydesign/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-6 h-12 rounded-full bg-ink text-ivory hover:bg-bordeaux transition-colors duration-hover"
                data-testid="instagram-cta"
              >
                <Instagram className="w-4 h-4" />
                <span className="text-sm tracking-wide">Vai al profilo</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-3 gap-3 md:gap-4">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <a
                    key={`ig-${n}`}
                    href="https://www.instagram.com/cirogallotti_jewerlydesign/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block aspect-square overflow-hidden rounded-2xl bg-secondary group relative image-shine"
                    aria-label="Apri profilo Instagram Ciro Gallotti"
                    data-testid={`instagram-photo-${n}`}
                  >
                    <img
                      src={c(`ig_${n}`, `/instagram-${n}.jpg`)}
                      alt="Creazione Ciro Gallotti — seguici su Instagram"
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 transition-colors duration-hover flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <Instagram className="w-6 h-6 text-ivory" />
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== SHOWROOM CTA ============== */}
      <section
        className="relative py-28 md:py-36 overflow-hidden bg-ink"
        data-testid="cta-section"
      >
        <div className="absolute inset-0 opacity-30 overflow-hidden" aria-hidden="true">
          <LoopVideo
            src={c('cta_video', '/cta-loop.mp4')}
            poster={c('cta_poster', '/footer-poster.jpg')}
            className="absolute inset-0 w-full h-full object-cover"
            testid="cta-video"
          />
        </div>
        {/* Overlay scuro per garantire la leggibilità del testo */}
        <div className="absolute inset-0 bg-ink/55" aria-hidden="true" />

        <div className="relative z-10 max-w-container mx-auto px-6 md:px-12 lg:px-16 text-center">
          <p className="text-eyebrow text-ivory mb-6">{c('cta_eyebrow', 'Esperienza Esclusiva')}</p>
          <h2 className="font-display text-h1 text-ivory mb-6 leading-tight">
            {c('cta_title', 'Vieni nel Nostro Showroom')}
          </h2>
          <p className="text-ivory text-lg max-w-xl mx-auto mb-10 leading-relaxed">
            {c('cta_subtitle', "Prenota un appuntamento privato per scoprire le nostre collezioni esclusive e vivere un'esperienza unica nel cuore di Napoli.")}
          </p>
          <Link to="/appuntamento" data-testid="cta-appuntamento">
            <Button size="xl" variant="accent">
              {c('cta_button', 'Prenota Ora')}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
